import { useTranslation } from 'react-i18next';

import { ChevronRightIcon } from '@heroicons/react/24/outline';
import cx from 'classnames';

import CreateAction from '~/components/Actions/CreateAction';
import PostImage from '~/components/PostCardv2/PostImage';
import PostLink from '~/components/PostCardv2/PostLink';
import Titles from '~/components/PostCardv2/Titles';
import Typography from '~/components/Typography';
import useTypedLoaderData from '~/hooks/useUtils/useTypedLoaderData';
import type { HomePageLoaderData } from '~/routes';
import normalizeFontType from '~/utils/normalizeFontType';

const getHeaderPosts = (posts: any[], featuredPosts: any[]) => {
  let headliner: any;
  const rightPanelPosts: any[] = [];

  featuredPosts.forEach((post, index) => {
    if (index === 0) {
      headliner = post;
    } else {
      rightPanelPosts.push(post);
    }
  });

  // Ensure that the posts have been properly filled out for aesthetic by falling back to regular posts
  let rightPanelPostsCount = rightPanelPosts.length;
  posts.forEach((post) => {
    if (!headliner) {
      headliner = post;
      return;
    }

    if (rightPanelPostsCount >= 4) {
      return;
    }

    rightPanelPosts.push(post);
    rightPanelPostsCount++;
  });

  return {
    headliner,
    rightPanelPosts,
  };
};

const Headliner = () => {
  const { t } = useTranslation();
  const { homePage, publication, featuredPosts, paginatedPosts } = useTypedLoaderData<HomePageLoaderData>();

  const { headliner, rightPanelPosts } = getHeaderPosts(paginatedPosts.posts, featuredPosts);

  const width = homePage?.general_section?.header?.width;

  return (
    <div className="px-4">
      <div className={cx('mx-auto mb-12 pr-4 pt-8', width === 'full' ? 'w-full' : 'w-full max-w-6xl')}>
        <div className="flex flex-col gap-0 md:flex-row md:gap-8">
          <div className="w-full md:w-2/3">
            {headliner && (
              <PostLink
                slug={headliner?.slug}
                className={cx('group relative flex w-full flex-col overflow-hidden rounded shadow-xl')}
              >
                <PostImage alt={headliner?.web_title} src={headliner?.image_url} radiusClass={'rounded-xs'} />
                <div className="absolute left-6 top-6">
                  <div
                    style={{
                      backgroundColor: homePage?.general_section?.theme?.accent_color,
                    }}
                    className="px-2 py-1"
                  >
                    <span className="font-semibold capitalize text-white">
                      {headliner?.content_tags?.[0]?.name || t('posts.featured')}
                    </span>
                  </div>
                </div>

                <div className="absolute bottom-0 flex h-full w-full flex-col justify-end space-y-1 bg-black bg-opacity-20 p-4 transition-all group-hover:bg-opacity-0">
                  <Typography
                    as="h2"
                    size="6xl"
                    fontFamily={homePage?.general_section?.theme?.font}
                    weight="bold"
                    color={'#ffffff'}
                  >
                    {headliner?.web_title}
                  </Typography>
                </div>
              </PostLink>
            )}
            <div className="pt-6">
              <CreateAction
                publication={publication}
                fontFamily={normalizeFontType(homePage.main_section.signup_widget.font)}
                placeholderText={homePage.main_section.signup_widget.placeholder_text}
                cta={homePage.main_section.signup_widget.cta_text}
                textColor={homePage.main_section.signup_widget.text_color}
                backgroundColor={homePage.main_section.signup_widget.bg_color}
              />
            </div>
          </div>

          <div className="w-full md:w-1/3">
            {rightPanelPosts.map((post, _index) => {
              return (
                <div
                  key={post.id}
                  className="border-b"
                  style={{
                    borderColor: homePage?.general_section?.theme?.border_color,
                  }}
                >
                  <PostLink
                    slug={post?.slug}
                    className={cx('group relative flex w-full flex-col space-y-2 py-5 transition-all')}
                  >
                    <div className="flex items-center justify-between space-x-4">
                      <div className="">
                        <Typography
                          as="span"
                          size="sm"
                          fontFamily={homePage?.general_section?.theme?.font}
                          weight="bold"
                          color={homePage?.general_section?.theme?.accent_color}
                          className="capitalize"
                        >
                          {post?.content_tags?.[0]?.name}
                        </Typography>
                        <Titles
                          title={post.web_title}
                          subtitle={post?.web_subtitle}
                          textClassName="group-hover:underline"
                          fontFamily={homePage?.general_section?.theme?.font}
                          titleSize="lg"
                          subtitleSize="xs"
                          titleColor={homePage?.general_section?.theme?.text_color}
                          subtitleColor={homePage?.general_section?.theme?.text_color}
                        />
                      </div>

                      <div
                        style={{
                          color: homePage?.general_section?.theme?.text_color,
                          borderColor: homePage?.general_section?.theme?.text_color,
                        }}
                        className="h-fit rounded-full border p-2"
                      >
                        <ChevronRightIcon className="h-6 w-6" />
                      </div>
                    </div>
                  </PostLink>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Headliner;
